import React, { RefObject } from 'react'
import { Box, Flex, Text, Link, Label, Message } from '@workwhile/ui'
import { User } from 'api/typings'
import {
  PaymentProvider,
  StripeProfileResponse,
  UserOutstandingBalance,
} from 'api/payments'
import { AmountEntry } from './AmountEntry'
import { BankAccountBox } from './BankAccountBox'

interface StripeBoxProps {
  user: User
  outstandingBalance: UserOutstandingBalance
  stripeProfile: StripeProfileResponse | null
  current1099PaymentProvider: PaymentProvider
  isTransitioningFromStripeToHighnote: boolean
  stripeBoxRef: RefObject<HTMLDivElement | null>
}

export const StripeBox = ({
  user,
  outstandingBalance,
  stripeProfile,
  current1099PaymentProvider,
  isTransitioningFromStripeToHighnote,
  stripeBoxRef,
}: StripeBoxProps) => {
  const isHighnote = current1099PaymentProvider === 'highnote'
  const stripeLink = user.stripeAccountIdProd
    ? `https://dashboard.stripe.com/connect/accounts/${user.stripeAccountIdProd}`
    : null

  // Highnote user with no Stripe payment history — onboarded directly
  const isDirectHighnoteOnboarding =
    isHighnote && !isTransitioningFromStripeToHighnote

  if (!stripeProfile && !stripeLink) {
    return (
      <Box
        ref={stripeBoxRef}
        position="relative"
        bg="white"
        border="1px solid"
        borderColor="border"
        borderRadius="medium"
        p={3}
        minWidth="250px"
        maxWidth="100%"
        width="100%"
        boxShadow="small"
        zIndex={2}
      >
        <Text fontWeight="bold" color="primary" fontSize={3}>
          No Stripe profile
        </Text>
      </Box>
    )
  }

  return (
    <Box
      ref={stripeBoxRef}
      position="relative"
      bg="white"
      border="1px solid"
      borderColor="border"
      borderRadius="medium"
      p={3}
      minWidth="250px"
      maxWidth="100%"
      width="100%"
      boxShadow="small"
      zIndex={2}
    >
      <Flex alignItems="center" gap={2} mb={2}>
        <Text fontWeight="bold" color="primary" fontSize={3}>
          Stripe{' '}
        </Text>
        <Label variant="primary">1099</Label>
        {current1099PaymentProvider === 'stripe' && (
          <Label variant="warning">Current 1099 Provider</Label>
        )}
      </Flex>

      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Text fontWeight="bold" fontSize={2}>
          Stripe Connect
        </Text>
        {stripeLink ? (
          <Link to={stripeLink} target="_blank">
            {user.stripeAccountIdProd}
          </Link>
        ) : null}
      </Flex>

      {isDirectHighnoteOnboarding ? (
        <Message variant="warning" borderRadius="small">
          User is onboarded to Highnote directly, no migration from Stripe.
        </Message>
      ) : (
        <>
          {isTransitioningFromStripeToHighnote ? (
            <Message variant="warning" mb={3} borderRadius="small">
              New payments will go to Highnote. Historical Stripe balances and
              payout accounts are shown below for reference.
            </Message>
          ) : null}

          {stripeProfile ? (
            <Flex flexDirection="row" gap={3}>
              <Box
                bg="background"
                border="1px solid"
                borderColor="border"
                borderRadius="small"
                p={3}
                flex={1}
              >
                <Text mb={3} fontSize={2} color="lightText">
                  Intermediate holding account
                </Text>
                <AmountEntry
                  label="Live balance:"
                  tooltip="Fetch from Stripe API"
                  labelProps={{ fontWeight: 'bold' }}
                  value={outstandingBalance.stripeBalanceLive}
                />
                <hr />
                <AmountEntry
                  label="Expected balance"
                  tooltip="Transfers - Payouts"
                  labelProps={{ fontWeight: 'bold' }}
                  value={
                    outstandingBalance.earningsWithStripe +
                    outstandingBalance.completedStripePayouts
                  }
                />
                <AmountEntry
                  ml={2}
                  labelProps={{ color: 'lightText' }}
                  label="Transfers"
                  value={outstandingBalance.earningsWithStripe}
                />
                <AmountEntry
                  ml={2}
                  labelProps={{ color: 'lightText' }}
                  label="Payouts"
                  value={outstandingBalance.completedStripePayouts}
                />
                <AmountEntry
                  ml={2}
                  labelProps={{ color: 'lightText', fontStyle: 'italic' }}
                  label="(Pending payouts)"
                  value={outstandingBalance.pendingStripePayouts}
                />
              </Box>
              <Box
                bg="background"
                border="1px solid"
                borderColor="border"
                borderRadius="small"
                p={3}
                minWidth="200px"
                flex={1}
              >
                <Text fontWeight="bold" fontSize={2} mb={2}>
                  Payout Options
                </Text>
                <Text color="lightText" mb={2}>
                  <Text fontWeight="bold" inline>
                    Instant Payout:
                  </Text>{' '}
                  User can request immediate payout if there is an
                  instant-eligible account
                </Text>
                <Text color="lightText" mb={2}>
                  <Text fontWeight="bold" inline>
                    Paycheck:
                  </Text>{' '}
                  Run weekly on Monday
                </Text>
                <Box>
                  {stripeProfile.externalBankAccounts
                    .sort((a, b) => (a.stripeIsDefault ? -1 : 1))
                    .map((account, index) => (
                      <BankAccountBox
                        key={index}
                        account={account}
                        badges={[
                          account.stripeIsDefault ? 'Default paycheck' : null,
                          account.stripeCanInstantPay
                            ? 'Instant eligible'
                            : null,
                        ]
                          .filter(Boolean)
                          .map((badge) => badge! as string)}
                      />
                    ))}
                  {stripeProfile.externalBankAccounts.length === 0 && (
                    <Text fontWeight="bold" color="error">
                      No accounts for payout
                    </Text>
                  )}
                </Box>
              </Box>
            </Flex>
          ) : (
            <Text color="lightText">No Stripe profile details available.</Text>
          )}
        </>
      )}
    </Box>
  )
}
